import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  getApiV5FirmsDocumentsDocumentUuidAnnotation,
  getGetApiV5FirmsDocumentsDocumentUuidAnnotationQueryKey,
  putApiV5FirmsDocumentsDocumentUuidAnnotation,
} from '@liscio/api';
import type { AnnotationsSource } from 'common/PSPDFKit/PSPDFViewer';

/**
 * Normalize PSPDFKit Instant JSON for a Liscio document before save/load.
 *
 * PSPDFKit tracks two references to the underlying PDF that become stale when
 * the stored bytes change server-side (AI ingestion, re-upload, etc.):
 *
 *  - top-level `pdfId` — triggers "Permanent PDF ID mismatch" on load
 *  - per-annotation `pdfObjectId` — triggers "Some annotations couldn't be
 *    deleted. (pdf object ids: {N})" during Instant JSON import
 *
 * In Liscio's model a Document record keeps its identity while its stored file
 * can legitimately change, so neither reference is meaningful. Stripping both
 * makes PSPDFKit treat every annotation as fresh (no reconciliation against
 * existing PDF objects) and lets hydration proceed against any revision of
 * the PDF.
 */
const normalizeInstantJson = (
  value: unknown,
): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') return null;
  const {
    pdfId: _pdfId,
    annotations,
    ...rest
  } = value as Record<string, unknown>;
  const cleanedAnnotations = Array.isArray(annotations)
    ? annotations.map((a) => {
        if (!a || typeof a !== 'object') return a;
        const { pdfObjectId: _pdfObjectId, ...ann } = a as Record<
          string,
          unknown
        >;
        return ann;
      })
    : annotations;
  return { ...rest, annotations: cleanedAnnotations };
};

/**
 * Returns a stable AnnotationsSource for PSPDFViewer, or undefined when the
 * document UUID isn't known yet. Safe to pass straight through to the viewer —
 * identity is memoized on documentUuid.
 */
export function useDocumentAnnotationsSource(
  documentUuid: string | null | undefined,
): AnnotationsSource | undefined {
  const queryClient = useQueryClient();

  return useMemo(() => {
    if (!documentUuid) return undefined;

    const key =
      getGetApiV5FirmsDocumentsDocumentUuidAnnotationQueryKey(documentUuid);

    return {
      load: async () => {
        const response = await queryClient.fetchQuery({
          queryKey: key,
          queryFn: () =>
            getApiV5FirmsDocumentsDocumentUuidAnnotation(documentUuid),
        });
        return normalizeInstantJson(response.data?.instant_json);
      },
      save: async (instantJson) => {
        // If the input isn't a recognizable object, skip the write rather
        // than overwrite the server's state with an empty blob.
        const safePayload = normalizeInstantJson(instantJson);
        if (!safePayload) return;
        const response = await putApiV5FirmsDocumentsDocumentUuidAnnotation(
          documentUuid,
          { instant_json: safePayload },
        );
        queryClient.setQueryData(key, response);
      },
    };
  }, [documentUuid, queryClient]);
}
